import React, { useState, useEffect, useCallback } from 'react';
import { obtenerDocumentosPorCaso, eliminarDocumento, TIPOS_DOCUMENTO } from '../../services/documentoService';
import ConfirmActionModal from './ConfirmActionModal';
import './ListaDocumentos.css';

const ListaDocumentos = ({ casoId }) => {
  const [documentos, setDocumentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState({
    isOpen: false,
    id: null,
    nombre: ''
  });

  const cargarDocumentos = useCallback(async () => {
    try {
      setLoading(true);
      const data = await obtenerDocumentosPorCaso(casoId);
      setDocumentos(data);
    } catch (error) {
      console.error('Error al cargar documentos:', error);
    } finally {
      setLoading(false);
    }
  }, [casoId]);

  useEffect(() => {
    if (casoId) {
      cargarDocumentos();
    }
  }, [casoId, cargarDocumentos]);

  const handleEliminarClick = (id, nombre) => {
    setConfirmDelete({
      isOpen: true,
      id: id,
      nombre: nombre
    });
  };

  const handleConfirmDelete = async () => {
    try {
      await eliminarDocumento(confirmDelete.id);
      cargarDocumentos();
      setConfirmDelete({ isOpen: false, id: null, nombre: '' });
    } catch (error) {
      alert('Error al eliminar: ' + error.message);
      setConfirmDelete({ isOpen: false, id: null, nombre: '' });
    }
  };

  const handleCancelDelete = () => {
    setConfirmDelete({ isOpen: false, id: null, nombre: '' });
  };

  const handleVerDocumento = (doc) => {
    if (doc.contenido) {
      const ventana = window.open();
      const htmlContent = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>${doc.nombreOriginal}</title>
            <meta charset="UTF-8">
            <style>
              body { margin: 0; padding: 0; font-family: sans-serif; background: #f5f5f5; }
              .container { width: 100%; height: 100vh; display: flex; align-items: center; justify-content: center; }
              iframe { width: 90%; height: 90%; border: none; background: white; box-shadow: 0 4px 12px rgba(0,0,0,0.1); border-radius: 8px; }
              .error { text-align: center; padding: 50px; color: #666; }
            </style>
          </head>
          <body>
            <div class="container">
              <iframe src="${doc.contenido}"></iframe>
            </div>
          </body>
        </html>
      `;
      ventana.document.write(htmlContent);
    } else if (doc.url) {
      window.open(doc.url, '_blank');
    } else {
      alert('No se puede visualizar este documento');
    }
  };

  const getTipoLabel = (tipo) => {
    const labels = {
      [TIPOS_DOCUMENTO.CONTRATO]: 'Contrato',
      [TIPOS_DOCUMENTO.IMPRONTA]: 'Improntas',
      [TIPOS_DOCUMENTO.FORMULARIO_TRASPASO]: 'Formulario Traspaso',
      [TIPOS_DOCUMENTO.INFORME_CESVI]: 'Informe Cesvi',
      [TIPOS_DOCUMENTO.SOAT]: 'SOAT',
      [TIPOS_DOCUMENTO.TARJETA_PROPIEDAD]: 'Tarjeta de Propiedad',
      [TIPOS_DOCUMENTO.OTRO]: 'Otro'
    };
    return labels[tipo] || 'Documento';
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (loading) return <div className="loading">Cargando documentos...</div>;

  return (
    <div className="lista-documentos">
      <h3>Documentos del Caso</h3>
      
      {documentos.length === 0 ? (
        <p className="sin-documentos">No hay documentos subidos</p>
      ) : (
        <div className="documentos-grid">
          {documentos.map(doc => (
            <div key={doc.id} className="documento-card">
              <div className="documento-icono">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M14 2v6h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="documento-info">
                <h4>{getTipoLabel(doc.tipo)}</h4>
                <p className="documento-nombre">{doc.nombreOriginal}</p>
                <div className="documento-metadata">
                  <span>{formatFileSize(doc.tamaño)}</span>
                  <span>{doc.fechaSubida?.toLocaleDateString()}</span>
                </div>
                {doc.descripcion && (
                  <p className="documento-descripcion">{doc.descripcion}</p>
                )}
              </div>
              <div className="documento-actions">
                <button 
                  onClick={() => handleVerDocumento(doc)}
                  className="btn-ver"
                  title="Ver documento"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2"/>
                    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                </button>
                <button 
                  onClick={() => handleEliminarClick(doc.id, doc.nombreOriginal)}
                  className="btn-eliminar"
                  title="Eliminar documento"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4 7h16M10 11v6M14 11v6M5 7l1 13a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2l1-13M9 7V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmActionModal
        isOpen={confirmDelete.isOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Confirmar eliminacion"
        message={`¿Estas seguro de que deseas eliminar el documento "${confirmDelete.nombre}"? Esta accion no se puede deshacer.`}
        confirmText="Si, eliminar"
        cancelText="Cancelar"
      />
    </div>
  );
};

export default ListaDocumentos;